'use strict'

import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { addDays, getWeekTypeForDate, toYmd, WORK_DAYS } from './scheduleUtils'

/**
 * Manages the review navigation flow: approved route days, review combos,
 * and navigation between combos.
 *
 * @param {{ url: string, selectedScenarioId: string, capacityMatrix: object, setFocusCrewId: Function, setFocusRouteId: Function, setFocusDate: Function }} config
 */
export default function useReviewNavigator ({ url, selectedScenarioId, capacityMatrix, setFocusCrewId, setFocusRouteId, setFocusDate }) {
  const [approvedRouteDays, setApprovedRouteDays] = useState([])
  const [reviewNavOpen, setReviewNavOpen] = useState(false)
  const [reviewComboIdx, setReviewComboIdx] = useState(0)
  const [skipApproved, setSkipApproved] = useState(false)

  const approvedSet = useMemo(() => {
    const s = new Set()
    approvedRouteDays.forEach(a => s.add(`${a.crewId}_${a.dayOfWeek}_${a.weekType || ''}`))
    return s
  }, [approvedRouteDays])

  const fetchApprovedRouteDays = useCallback(async () => {
    if (!selectedScenarioId) { setApprovedRouteDays([]); return }
    try {
      const resp = await axios.get(`${url}/api/hmi/territory/scenarios`)
      const scenario = (resp.data?.scenarios || []).find(s => s._id === selectedScenarioId)
      setApprovedRouteDays(scenario?.approvedRouteDays || [])
    } catch (_) { setApprovedRouteDays([]) }
  }, [selectedScenarioId, url])

  // Clear stale approvals immediately when scenario changes, then fetch fresh data
  useEffect(() => {
    setApprovedRouteDays([])
    fetchApprovedRouteDays()
  }, [fetchApprovedRouteDays])

  // Build all crew/day/weekType combos from capacityMatrix
  const reviewCombos = useMemo(() => {
    if (!capacityMatrix?.routes?.length) return []
    const weekOf = capacityMatrix.weekOf
    const combos = []
    const weekTypes = ['A', 'B']
    capacityMatrix.routes.forEach(route => {
      weekTypes.forEach(wt => {
        WORK_DAYS.forEach((dayName, dayIdx) => {
          let targetDate = null
          if (weekOf) {
            const baseMonday = new Date(weekOf + 'T00:00:00')
            const baseWt = getWeekTypeForDate(baseMonday)
            const monday = baseWt === wt ? baseMonday : addDays(baseMonday, 7)
            targetDate = toYmd(addDays(monday, dayIdx))
          }
          const key = `${route.crewId}_${dayName}_${wt}`
          combos.push({
            crewId: route.crewId,
            routeId: route.routeId,
            routeName: route.routeName,
            dayName,
            weekType: wt,
            key,
            targetDate,
            approved: approvedSet.has(key)
          })
        })
      })
    })
    return combos
  }, [capacityMatrix, approvedSet])

  const filteredReviewCombos = useMemo(() => {
    if (!skipApproved) return reviewCombos
    return reviewCombos.filter(c => !c.approved)
  }, [reviewCombos, skipApproved])

  // Clamp index when filteredReviewCombos shrinks
  useEffect(() => {
    if (filteredReviewCombos.length && reviewComboIdx >= filteredReviewCombos.length) {
      setReviewComboIdx(0)
    }
  }, [filteredReviewCombos.length, reviewComboIdx])

  const navigateToCombo = useCallback((combo) => {
    if (!combo) return
    if (combo.crewId) setFocusCrewId(combo.crewId)
    if (combo.routeId) setFocusRouteId(combo.routeId)
    if (combo.targetDate) setFocusDate(combo.targetDate)
  }, [setFocusCrewId, setFocusRouteId, setFocusDate])

  const reviewNavNext = useCallback(() => {
    if (!filteredReviewCombos.length) return
    const nextIdx = (reviewComboIdx + 1) % filteredReviewCombos.length
    setReviewComboIdx(nextIdx)
    navigateToCombo(filteredReviewCombos[nextIdx])
  }, [filteredReviewCombos, reviewComboIdx, navigateToCombo])

  const reviewNavPrev = useCallback(() => {
    if (!filteredReviewCombos.length) return
    const prevIdx = (reviewComboIdx - 1 + filteredReviewCombos.length) % filteredReviewCombos.length
    setReviewComboIdx(prevIdx)
    navigateToCombo(filteredReviewCombos[prevIdx])
  }, [filteredReviewCombos, reviewComboIdx, navigateToCombo])

  return {
    approvedRouteDays,
    setApprovedRouteDays,
    approvedSet,
    fetchApprovedRouteDays,
    reviewCombos,
    filteredReviewCombos,
    reviewNavOpen,
    setReviewNavOpen,
    reviewComboIdx,
    setReviewComboIdx,
    skipApproved,
    setSkipApproved,
    reviewNavNext,
    reviewNavPrev,
    navigateToCombo
  }
}
